/**
 * Действия над приёмом лекарства.
 *
 * Отметка приёма — это два связанных изменения: статус дозы и остаток упаковки.
 * Собраны здесь, потому что затрагивают две сущности сразу, а сущности друг о
 * друге знать не должны.
 */

import {
  logExtraDose,
  logPlannedDose,
  markDoseSkipped,
  markDoseTaken,
  postponeDose,
  rescheduleDose,
  resetDose,
} from '@/entities/dose/model/use-doses';
import { doseEventsRepo } from '@/entities/dose/model/repository';
import { takenMomentOf } from '@/entities/medication/model/dose-status';
import { adjustStock } from '@/entities/medication/model/use-medications';
import { db } from '@/shared/db/database';
import type { DoseEvent, Medication, SkipReason } from '@/shared/db/schema';
import { type IsoDate, type MinutesOfDay, momentAt } from '@/shared/lib/datetime';

/** На сколько минут откладывается приём по кнопке «Отложить». */
export const SNOOZE_MINUTES = 15;

/**
 * Статус дозы и остаток упаковки меняются вместе.
 *
 * Раньше это были две независимые транзакции, и сбой между ними оставлял дозу
 * отмеченной, а упаковку — нетронутой. Для учёта остатка это тихая ошибка:
 * приложение считает, что таблеток больше, чем есть, и не предупредит о том,
 * что лекарство заканчивается.
 */
async function withStock(
  medication: Medication | undefined,
  delta: number,
  change: () => Promise<void>,
  /**
   * Доза, статус которой меняется, и признак «изменение уже применено».
   * Без него повторная отметка списывала из упаковки ещё раз.
   */
  guard?: { dose: DoseEvent; applied: (current: DoseEvent) => boolean },
): Promise<void> {
  await db.transaction('rw', db.doseEvents, db.medications, db.outbox, async () => {
    if (guard) {
      // Статус перечитывается внутри транзакции. Списание остатка — это дельта
      // от текущего значения, и о прежнем статусе дозы оно ничего не знает:
      // два нажатия по «Принял» вычитали две таблетки вместо одной. Отметить
      // одну и ту же дозу можно из ленты приёмов, из сетки календаря и из
      // уведомления, поэтому проверка стоит здесь, а не на кнопках.
      const current = await db.doseEvents.get(guard.dose.id);
      if (!current || current.deletedAt !== null) return;
      if (guard.applied(current)) return;
    }

    await change();
    if (medication) await adjustStock(medication, delta);
  });
}

/** Приём уже отмечен принятым — повторять списание нечего. */
const alreadyTaken = (current: DoseEvent) => current.status === 'taken';

/**
 * «Принял»: статус меняется, из упаковки списывается плановое количество.
 * Списание идёт только там, где пользователь ведёт учёт остатка.
 */
export async function takeDose(dose: DoseEvent, medication: Medication | undefined): Promise<void> {
  // Перенесённый на утро приём отмечается утренним временем, а не моментом
  // нажатия: человек уже сказал, когда его принял, — правило живёт в
  // `takenMomentOf`, чтобы календарь и лента отвечали одинаково.
  await withStock(
    medication,
    -dose.plannedAmount,
    () => markDoseTaken(dose, { takenAt: takenMomentOf(dose) }),
    {
      dose,
      applied: alreadyTaken,
    },
  );
}

/**
 * Внеплановый приём: отдельная запись, расписание курса не меняется.
 *
 * Время указывает человек, поэтому и в журнал оно должно попасть его —
 * до этой функции экран записывал момент нажатия кнопки, и приём, отмеченный
 * вечером как утренний, выглядел в журнале вечерним.
 */
export async function addExtraDose(
  medication: Medication,
  date: IsoDate,
  timeMin: MinutesOfDay,
): Promise<void> {
  await withStock(medication, -medication.doseAmount, () =>
    logExtraDose(medication.id, date, timeMin, medication.doseAmount, momentAt(date, timeMin)),
  );
}

/**
 * «Принял, но раньше»: приём отмечается задним числом с настоящим временем.
 *
 * Плановое время дозы не меняется — расписание остаётся расписанием, а факт
 * попадает в `takenAt`. Так журнал показывает и то, что было назначено, и то,
 * что произошло: без этой пары нельзя понять, человек систематически опаздывает
 * или расписание ему не подходит.
 */
export async function takeDoseAt(
  dose: DoseEvent,
  medication: Medication | undefined,
  timeMin: MinutesOfDay,
): Promise<void> {
  await withStock(
    medication,
    -dose.plannedAmount,
    () => markDoseTaken(dose, { takenAt: momentAt(dose.date, timeMin) }),
    { dose, applied: alreadyTaken },
  );
}

/**
 * Намеченный приём курса «по необходимости».
 *
 * Из упаковки ничего не списывается: таблетку ещё не выпили. Списание придёт
 * с отметкой «Принял» — там же, где и у любого другого приёма.
 */
export async function planDose(
  medication: Medication,
  date: IsoDate,
  timeMin: MinutesOfDay,
): Promise<void> {
  await logPlannedDose(medication.id, date, timeMin, medication.doseAmount);
}

/**
 * «Приму в другое время»: приём этого дня переезжает на выбранный час.
 *
 * Отличие от внепланового приёма — в том, что записи не прибавляется: доза одна
 * и та же, просто ждут её теперь в другое время. Отличие от кнопки «Отложить» —
 * только в том, что час называет человек, а не прибавляются четверть часа.
 */
export async function moveDose(dose: DoseEvent, timeMin: MinutesOfDay): Promise<void> {
  await rescheduleDose(dose, momentAt(dose.date, timeMin));
}

/**
 * Правка времени уже принятой дозы.
 *
 * Отметка «Принял» ставит момент нажатия, а таблетку нередко выпили раньше.
 * В сетке дня это видно как блок не на своём месте, и поправить его перетас-
 * киванием — самое естественное движение. Остаток упаковки не трогается:
 * таблетка уже списана, меняется только время в журнале.
 */
export async function correctDoseTime(dose: DoseEvent, timeMin: MinutesOfDay): Promise<void> {
  await markDoseTaken(dose, {
    actualAmount: dose.actualAmount ?? dose.plannedAmount,
    takenAt: momentAt(dose.date, timeMin),
  });
}

export async function skipDose(dose: DoseEvent, reason: SkipReason): Promise<void> {
  await markDoseSkipped(dose, reason);
}

export async function snoozeDose(dose: DoseEvent, minutes = SNOOZE_MINUTES): Promise<void> {
  await postponeDose(dose, minutes);
}

/**
 * Удаление записи о приёме.
 *
 * Отличие от «Отменить отметку» — в том, что записи не остаётся вовсе.
 * Отмена говорит «ещё не принял», и приём возвращается в ожидание; удаление
 * говорит «этого приёма не было»: записали не тот препарат, отметили дважды,
 * нажали по ошибке. Для журнала, который показывают врачу, разница
 * принципиальная — там не должно стоять того, чего не происходило.
 *
 * Списанная таблетка возвращается в упаковку: раз приёма не было, то и
 * расходовать было нечего.
 *
 * Расписание при этом остаётся расписанием. Если курс ждал приёма в это время,
 * он появится снова — уже как предстоящий, потому что удалили запись о факте,
 * а не назначение врача.
 */
export async function deleteDose(
  dose: DoseEvent,
  medication: Medication | undefined,
): Promise<void> {
  const returned = dose.status === 'taken' ? (dose.actualAmount ?? dose.plannedAmount) : 0;

  await withStock(returned > 0 ? medication : undefined, returned, () =>
    doseEventsRepo.remove(dose.id),
  );
}

/** Отмена ошибочной отметки: статус сбрасывается, списанное возвращается. */
export async function undoDose(dose: DoseEvent, medication: Medication | undefined): Promise<void> {
  const returned = dose.status === 'taken' ? (dose.actualAmount ?? dose.plannedAmount) : 0;
  // Зеркально отметке: вернуть в упаковку можно только то, что было списано,
  // и только один раз — повторная отмена уже сброшенной дозы ничего не меняет.
  await withStock(returned > 0 ? medication : undefined, returned, () => resetDose(dose), {
    dose,
    applied: (current) => current.status !== 'taken',
  });
}
